import { Bounds, GizmoHelper, GizmoViewport, Line, OrbitControls, useBounds } from '@react-three/drei';
import { Canvas, type ThreeEvent } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import type { BoundaryChain, EditorDocument, PaintLeaf, Vec3 } from '../core/types';
import { useAppStore } from '../app/store';

interface ViewportProps { document: EditorDocument; revision: number }

interface LeafMeshData {
  key: string;
  partIndex: number;
  extruder: number;
  geometry: THREE.BufferGeometry;
  leafIds: number[];
}

function buildMeshes(document: EditorDocument): LeafMeshData[] {
  const output: LeafMeshData[] = [];
  document.parts.forEach((part, partIndex) => {
    const grouped = new Map<number, PaintLeaf[]>();
    for (const leaf of part.leaves) {
      const list = grouped.get(leaf.extruder); if (list) list.push(leaf); else grouped.set(leaf.extruder, [leaf]);
    }
    for (const [extruder, leaves] of grouped) {
      const positions = new Float32Array(leaves.length * 9);
      const normals = new Float32Array(leaves.length * 9);
      leaves.forEach((leaf, faceIndex) => {
        for (let vertex = 0; vertex < 3; vertex += 1) {
          positions.set(leaf.vertices[vertex], faceIndex * 9 + vertex * 3);
          normals.set(leaf.normal, faceIndex * 9 + vertex * 3);
        }
      });
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
      geometry.computeBoundingSphere();
      output.push({ key: `${part.key}:${partIndex}:${extruder}`, partIndex, extruder, geometry, leafIds: leaves.map((leaf) => leaf.id) });
    }
  });
  return output;
}

function ModelMeshes({ document, revision }: ViewportProps) {
  const fill = useAppStore((state) => state.fill);
  const meshes = useMemo(() => buildMeshes(document), [document, revision]);
  useEffect(() => () => meshes.forEach((mesh) => mesh.geometry.dispose()), [meshes]);
  const colors = new Map(document.filaments.map((filament) => [filament.index, filament.color]));
  return <>
    {meshes.map((mesh) => <mesh
      key={mesh.key}
      geometry={mesh.geometry}
      castShadow
      receiveShadow
      onPointerDown={(event: ThreeEvent<PointerEvent>) => {
        if (event.button !== 0 || event.faceIndex == null) return;
        event.stopPropagation();
        fill(mesh.partIndex, mesh.leafIds[event.faceIndex]);
      }}
    >
      <meshStandardMaterial color={colors.get(mesh.extruder) || '#8A919A'} roughness={0.68} metalness={0.02} side={THREE.DoubleSide} />
    </mesh>)}
  </>;
}

function BoundaryLines({ document }: { document: EditorDocument }) {
  const mode = useAppStore((state) => state.boundaryMode);
  const selectedId = useAppStore((state) => state.selectedBoundaryId);
  const previews = useAppStore((state) => state.previewBoundaries);
  const select = useAppStore((state) => state.selectBoundary);
  const previewMap = useMemo(() => new Map(previews.map((chain) => [chain.id, chain])), [previews]);
  const currentChains = useMemo(() => document.boundaries.map((chain) => previewMap.get(chain.id) ?? chain), [document.boundaries, previewMap]);
  const selectedGroup = document.boundaryGroups.find((group) => group.id === selectedId);
  const selectedChainIds = useMemo(() => new Set(selectedGroup?.chainIds ?? []), [selectedGroup]);
  const smoothData = useMemo(() => lineSegmentsFor(currentChains, selectedChainIds, false), [currentChains, selectedChainIds]);
  const originalData = useMemo(() => lineSegmentsFor(document.boundaries, selectedChainIds, true), [document.boundaries, selectedChainIds]);
  const selected = currentChains.filter((chain) => selectedChainIds.has(chain.id));
  useEffect(() => () => { smoothData.geometry.dispose(); originalData.geometry.dispose(); }, [smoothData, originalData]);
  if (mode === 'off') return null;
  return <>
    {mode === 'compare' && <lineSegments geometry={originalData.geometry} raycast={() => undefined}>
      <lineDashedMaterial color="#8F98A4" dashSize={0.6} gapSize={0.35} transparent opacity={0.65} />
    </lineSegments>}
    <lineSegments
      geometry={smoothData.geometry}
      onClick={(event) => {
        event.stopPropagation();
        const id = smoothData.chainIds[event.index ?? 0];
        if (id) select(id);
      }}
    ><lineBasicMaterial color="#E8EDF2" /></lineSegments>
    {selected.map((chain) => <Line key={chain.id} points={closePoints(chain.smoothedPoints, chain.closed)} color="#65B5FF" lineWidth={3} />)}
  </>;
}

function closePoints(points: Vec3[], closed: boolean): Vec3[] {
  return closed && points.length ? [...points, points[0]] : points;
}

function lineSegmentsFor(chains: BoundaryChain[], excludedIds: Set<string>, original: boolean): { geometry: THREE.BufferGeometry; chainIds: string[] } {
  const positions: number[] = []; const chainIds: string[] = [];
  for (const chain of chains) {
    if (excludedIds.has(chain.id)) continue;
    const points = original ? chain.originalPoints : chain.smoothedPoints;
    const count = chain.closed ? points.length : points.length - 1;
    for (let index = 0; index < count; index += 1) {
      positions.push(...points[index], ...points[(index + 1) % points.length]);
      chainIds.push(chain.id);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  return { geometry, chainIds };
}

function Scene(props: ViewportProps) {
  return <>
    <color attach="background" args={['#101216']} />
    <ambientLight intensity={1.2} />
    <directionalLight position={[4, -5, 8]} intensity={2.2} castShadow />
    <directionalLight position={[-5, 3, 1]} intensity={0.65} />
    <gridHelper args={[400, 40, '#343941', '#24282e']} rotation={[Math.PI / 2, 0, 0]} />
    <Bounds fit clip observe margin={1.22}>
      <ModelMeshes {...props} />
      <BoundaryLines document={props.document} />
      <FitViewController />
    </Bounds>
    <OrbitControls makeDefault enableDamping dampingFactor={0.08} />
    <GizmoHelper alignment="bottom-right" margin={[72, 64]}>
      <GizmoViewport axisColors={['#da6b6b', '#65b88a', '#6fa4d9']} labelColor="#dce2e8" />
    </GizmoHelper>
  </>;
}

function FitViewController() {
  const bounds = useBounds();
  useEffect(() => {
    const orient = (event: Event) => {
      const view = (event as CustomEvent<string>).detail || 'fit';
      bounds.refresh().clip();
      if (view === 'fit') { bounds.fit(); return; }
      const { center, distance } = bounds.getSize();
      const directions: Record<string, THREE.Vector3> = {
        iso: new THREE.Vector3(1, -1, 0.78),
        front: new THREE.Vector3(0, -1, 0),
        top: new THREE.Vector3(0, 0, 1),
      };
      const direction = (directions[view] || directions.iso).normalize();
      const position = center.clone().addScaledVector(direction, distance);
      bounds.moveTo(position).lookAt({ target: center, up: view === 'top' ? [0, 1, 0] : [0, 0, 1] });
    };
    window.addEventListener('3mf-camera-view', orient);
    return () => window.removeEventListener('3mf-camera-view', orient);
  }, [bounds]);
  return null;
}

export function ModelViewport(props: ViewportProps) {
  const canvas = useRef<HTMLDivElement>(null);
  return <div className="viewport" ref={canvas}>
    <Canvas camera={{ position: [110, -130, 90], up: [0, 0, 1], fov: 42, near: 0.01, far: 100000 }} dpr={[1, 2]} gl={{ antialias: true }}>
      <Scene {...props} />
    </Canvas>
    <div className="viewport-controls">
      {['iso', 'front', 'top', 'fit'].map((view) => <button key={view} onClick={() => window.dispatchEvent(new CustomEvent('3mf-camera-view', { detail: view }))}>{view === 'iso' ? 'Isometric' : view[0].toUpperCase() + view.slice(1)}</button>)}
    </div>
    <div className="viewport-hint">LMB paint · drag orbit · RMB pan · wheel zoom</div>
  </div>;
}
